import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Search, ShoppingCart } from 'lucide-react';
import { cn } from '@/lib/utils';

// Logo URL from Media Library
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ccc-site-rebuild/artifacts/haq1vved_logo2-e1721957800363.png';

const navigation = [
  { name: 'HOME', href: '/' },
  { 
    name: 'ABOUT US', 
    href: '/about-us',
    children: [
      { name: 'About Us', href: '/about-us' },
      { name: 'Leadership', href: '/leadership' },
      { name: 'Contact', href: '/contact' },
    ]
  },
  { 
    name: 'NEXT STEPS', 
    href: '/next-steps',
    children: [
      { name: 'Next Steps', href: '/next-steps' },
      { name: 'Plan of Salvation', href: '/plan-of-salvation' },
      { name: 'Baptism', href: '/baptism' },
      { name: 'Membership', href: '/membership' },
      { name: 'Partnership', href: '/partnership' },
      { name: 'Volunteer', href: '/volunteer' },
      { name: 'Prayer Request', href: '/prayer-request' },
      { name: 'Resource Center', href: '/resources' },
    ]
  },
  { 
    name: 'OUR MINISTRIES', 
    href: '/our-ministries',
    children: [
      { name: 'All Ministries', href: '/our-ministries' },
      { name: 'Kingdom Kids', href: '/ministries/kingdom-kids' },
      { name: 'Kingdom Students', href: '/ministries/kingdom-students' },
      { name: 'Worship', href: '/ministries/worship' },
      { name: "Women's Ministry", href: '/ministries/womens-ministry' },
      { name: 'Missions', href: '/ministries/missions' },
      { name: 'LifeGroups', href: '/ministries/lifegroups' },
    ]
  },
  { name: 'EVENTS', href: '/events' },
  { name: 'SERMONS', href: '/sermons' },
  { name: 'WATCH LIVE', href: '/live' },
  { name: 'GIVING OPTIONS', href: '/onlinegiving' },
];

const TITHELY_FORM_ID = 'bd9e646f-97f2-4ecc-990e-94c9cdb1cef9';

export default function Header({ variant = 'dark' }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const isDark = variant === 'dark';

  const handleGiveOnline = () => {
    window.open(`https://give.tithe.ly/?formId=${TITHELY_FORM_ID}`, '_blank');
  };

  return (
    <header className={cn(
      'absolute top-0 left-0 right-0 z-50 transition-all duration-300',
      isDark ? 'bg-transparent' : 'bg-white shadow-sm'
    )}>
      {/* Tithely Script */}
      <script src="https://tithe.ly/widget/give.js?3" />
      
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Holy Temple CCC" 
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <div 
                key={item.name} 
                className="relative group"
                onMouseEnter={() => item.children && setOpenDropdown(item.name)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={cn(
                    'nav-link px-3 py-2 flex items-center gap-1',
                    isDark ? 'nav-link-dark' : 'nav-link-light',
                    location.pathname === item.href && 'border-b-2 border-current'
                  )}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                  {item.children && <ChevronDown className="w-3 h-3" />}
                </Link>
                
                {/* Dropdown */}
                {item.children && (
                  <div className={cn(
                    'absolute top-full left-0 bg-white shadow-lg rounded-md py-2 min-w-48 transition-all duration-200',
                    openDropdown === item.name ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                  )}>
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.href}
                        className="dropdown-item"
                        data-testid={`nav-dropdown-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <button 
              className={cn('p-2', isDark ? 'text-white' : 'text-gray-700')}
              data-testid="search-button"
            >
              <Search className="w-5 h-5" />
            </button>
            <button 
              className={cn('p-2', isDark ? 'text-white' : 'text-gray-700')}
              data-testid="cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
            <button
              onClick={handleGiveOnline}
              className="btn-primary text-sm"
              data-testid="give-online-button"
            >
              GIVE ONLINE
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className={cn('w-6 h-6', isDark ? 'text-white' : 'text-gray-900')} />
            ) : (
              <Menu className={cn('w-6 h-6', isDark ? 'text-white' : 'text-gray-900')} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="container mx-auto px-4 py-4">
            {navigation.map((item) => (
              <div key={item.name} className="border-b border-gray-100 last:border-0">
                <Link
                  to={item.href}
                  className="block py-3 text-gray-800 font-medium"
                  onClick={() => !item.children && setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
                {item.children && (
                  <div className="pl-4 pb-2">
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.href}
                        className="block py-2 text-gray-600 text-sm"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => { handleGiveOnline(); setMobileMenuOpen(false); }}
              className="btn-primary w-full mt-4 text-sm"
            >
              GIVE ONLINE
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
